import { inject } from '@angular/core';
import { cloneDeep } from 'lodash';
import { UserService } from 'src/app/_services/users/user.service';
import { isNullOrUndefined } from 'src/app/_shared/shared.functions';
import { BotMessage, MessageType } from 'src/app/chat-ai/models/chat-ai.models';
import { ChatAiStateService } from 'src/app/chat-ai/services/state/chat-ai-state.service';

export const GENERAL_ERR_MSG = 'Could not find the right answer. Please, try to ask something else.';
export const GENERAL_NO_ANSWER_MSG = "Alright, please let me know if there's anything else I can assist you with.";

const YES_ANSWERS: string[] = [
    'yes',
    'yep',
    'ok',
    'alright',
    'got it',
    'perfect',
    'excellent',
    'looks good',
    'looks great',
    'definitely',
    'absolutely',
    'by all means',
    'works for me',
    "that's fine",
    "that's great",
    "that's perfect",
    'you got it',
    'consider it done',
    "i'm on board",
    'i agree',
    'approve',
    'will do',
    'okay',
    "okay, let's do it",
    "okay let's do it",
    "ok let's do it",
    "ok, let's do it"
];

export class MessageProvider {
    private readonly messageMap: { [keywords: string]: string } = {
        'show,me,customer,customers,client,clients,profile,profiles': SHOW_CUSTOMER_PROFILES_TEMPLATE_MESSAGE,
        yes_1: REVIEW_CASE_ANALYSIS_TEMPLATE_MESSAGE,
        yes_2: RECOMMENDED_STEPS_TEMPLATE_MESSAGE,
        'update,and,assign': CASE_UPDATED_AND_ASSIGNED_TEMPLATE_MESSAGE,
        yes_3: LOWER_RISK_CLIENT_REVIEW_TEMPLATE_MESSAGE,
        yes_4: FINISH_UPDATING_INVESTIGATION_SUMMARY_TEMPLATE_MESSAGE
    };
    private readonly minMatchesCount = 2;
    private readonly userService: UserService = inject(UserService);
    private readonly state: ChatAiStateService = inject(ChatAiStateService);

    getDefaultMessage(): string {
        return FIRST_DEMO_TEMPLATE_MESSAGE.replace(
            '{{name}}',
            this.userService.user?.firstName ?? this.userService.user?.lastName ?? ''
        );
    }

    getMessage(query: string): string {
        if (isNullOrUndefined(query)) {
            return '';
        }

        if (YES_ANSWERS.includes(query.toLowerCase())) {
            const lastBotMsg: string = cloneDeep(this.state.$messages())
                .reverse()
                .find(
                    m =>
                        m.type === MessageType.Bot &&
                        (m as BotMessage).isStreaming === false &&
                        ![GENERAL_ERR_MSG, GENERAL_NO_ANSWER_MSG].includes(m.text)
                )?.text;
            if (lastBotMsg?.includes('Given its high notional value, would you prefer to review the analysis')) {
                return this.messageMap['yes_1'];
            } else if (lastBotMsg?.includes('Would you like to review the recommended next steps, based on policy?')) {
                return this.messageMap['yes_2'];
            } else if (lastBotMsg?.includes('Ready to review the lower risk client now?')) {
                return this.messageMap['yes_3'];
            } else if (lastBotMsg?.includes('Would you like to close and use this as the investigation summary?')) {
                return this.messageMap['yes_4'];
            }

            return GENERAL_ERR_MSG;
        } else if (query.toLowerCase().includes('no')) {
            return GENERAL_NO_ANSWER_MSG;
        }

        const textArray: string[] = this.getWords(query);
        const allKeywords: string[][] = Object.keys(this.messageMap).map(keyword => keyword.split(','));
        const resultMap: { [index: string]: number } = {};

        for (let keywordIndex = 0; keywordIndex < allKeywords.length; keywordIndex++) {
            const keywords: string[] = allKeywords[keywordIndex];
            const matchedWords: string[] = [];

            for (let i = 0; i < textArray.length; i++) {
                const word = textArray[i].toLowerCase();

                if (!matchedWords.includes(word)) {
                    if (keywords.includes(word)) {
                        matchedWords.push(word);
                    }
                }
            }

            for (let i = 0; i < keywords.length; i++) {
                const term = keywords[i];

                if (!matchedWords.includes(term)) {
                    if (query.toLowerCase().includes(term)) {
                        matchedWords.push(term);
                    }
                }
            }

            resultMap[keywords.join(',')] = matchedWords.length;
        }

        const sortableResultMap = Object.fromEntries(Object.entries(resultMap).sort(([, a], [, b]) => b - a));

        if (
            Object.keys(sortableResultMap).length === 0 ||
            Object.values(sortableResultMap)[0] <= this.minMatchesCount
        ) {
            return GENERAL_ERR_MSG;
        }

        return this.messageMap[Object.keys(sortableResultMap)[0]];
    }

    private getWords(str) {
        const words: string[] = str.toLowerCase().split(' ');
        const uniqueWords: string[] = [...new Set(words)];

        for (let i = 0; i < uniqueWords.length; i++) {
            uniqueWords[i] = uniqueWords[i].replace(/-/g, ' ');
        }

        return uniqueWords;
    }
}

export const FIRST_DEMO_TEMPLATE_MESSAGE = `<div>
    <span class="title">Hey {{name}}, flagging this for your attention — I’ve noticed a spike in alerts on the Delta One desk, especially around NVIDIA:</span>
    <ul>
        <li>Momentum Ignition: 3 alerts</li>
        <li>Touting: 1 alert</li>
        <li>Unusual Volume: 3 alerts</li>
    </ul>
    <span>No market-wide price movement was detected.</span>
</div>`;

export const SHOW_CUSTOMER_PROFILES_TEMPLATE_MESSAGE = `<div>
    <span class="font-size-18">All alerts are generated for two low-risk customers in the institutional segment.</span>
    <div>Customer ID: <a class="link" href="https://dashboard.uat-eu-central-1.soliduslabs.app/ucv/003b7834b373a4be97ec57306fc31cae" target="_blank">003b7834b373a4be97ec57306fc31cae</a></div>
    <ul>
        <li>Risk: <span class="score-low">Low</span></li>
        <li>Client Jurisdiction: US</li>
        <li>Active since: April 2021</li>
        <li>Average Daily Notional: $9,987</li>
        <li>Today: $20,701 (~2 x baseline)</li>
        <li>No historical alerts past 6 months</li>
    </ul>
    <div>Customer ID: <a class="link" href="https://dashboard.uat-eu-central-1.soliduslabs.app/ucv/99912123" target="_blank">99912123</a></div>
    <ul>
        <li>Risk: <span class="score-low">Low</span></li>
        <li>Client Jurisdiction: US</li>
        <li>Active since: April 2018</li>
        <li>Average Daily Notional: $63,060</li>
        <li>Today: $1,324,263 <span class="score-high">(~20 x baseline)</span></li>
        <li><span class="score-high">4 Similar alerts in the past 3 months</span></li>
    </ul>
    <div>Given its high notional value, would you prefer to review the analysis of <a href="https://dashboard.uat-eu-central-1.soliduslabs.app/ucm/cases/C2503110MNODJT" class="link" target="_blank">C2503110MNODJT</a> first?</div>
</div>`;

export const REVIEW_CASE_ANALYSIS_TEMPLATE_MESSAGE = `<div>
    <div>Starting Initial Investigation...</div>
    <div>Comparing historical alert patterns for these clients...</div>
    <div>Analyzing client’s trading behaviour patterns...</div>
    <div>Reviewing algo thresholds...</div>
    <br>
    <span>Here’s my analysis for Momentum Ignition Alert. Client usually trades ~ $63K a day, but sold $1.3M NVDA/USD-CFD in 8 seconds.</span>
    <ul>
        <li>Pushed the price by down by 2%</li>
        <li>~20x their normal 20 day ADV</li>
        <li>In-window PnL $6.4 K, suggest profit motivated price shaping</li>
        <li>4th similar “push the price” alert in the past 6 weeks (7 in the past year) - each time a fast sell drives the price and then nets profit quickly.</li>
        <li>Order to Cancel ratio jumps from 4:1 (past 3 months), to 10:1 on the alert day, high signal</li>
        <li>NVDA/USD-CFD now makes up their up to 57% of their flow (was 18% last month), showing a risky tilt to one name</li>
    </ul>
    <div>Together with the oversized volume, heavy cancellations, activity looks like a Momentum Ignition</div>
    <div>These insights are compiled, into this case <a href="https://dashboard.uat-eu-central-1.soliduslabs.app/ucm/cases/C2503110MNODJT" class="link" target="_blank">C2503110MNODJT</a></div>
    <span>Would you like to review the recommended next steps, based on policy?</span>
</div>`;

export const RECOMMENDED_STEPS_TEMPLATE_MESSAGE = `<div>
    <span>Here are my recommendation:</span>
    <ul>
        <li>Immediate Outreach: Contact the Account Manager for details and log the rationale within 24 hours</li>
        <li>Assign to Sara (Analyst Team Lead) for further analysis & oversight and review the trades</li>
        <li>If intent cannot be disproved, draft a SAR narrative within 30 days.</li>
    </ul>
    <div>Should I go ahead and update the investigation summary on this case?</div>
    <br>
    <div class="buttons">
        <button class="button" type="button">Update and Assign</button>
    </div>
</div>`;

export const CASE_UPDATED_AND_ASSIGNED_TEMPLATE_MESSAGE = `<div>
    <div>Assigned and case summary is updated!</div>
    <div>Ready to review the lower risk client now?</div>
</div>`;

export const LOWER_RISK_CLIENT_REVIEW_TEMPLATE_MESSAGE = `<div>
    <ul>
        <li>Client <a class="link" href="https://dashboard.uat-eu-central-1.soliduslabs.app/ucv/003b7834b373a4be97ec57306fc31cae" target="_blank">003b7834b373a4be97ec57306fc31cae</a> traded about 2x their normal, 20 day notional, breaching the 20 K/ 15min threshold window.</li>
        <li>Client’s trades were less than 0.5% market volume, and were made with slow orders that didn’t push the price.</li>
        <li>Client’s Jurisdiction: US</li>
        <li>Active since: April 2021</li>
        <li>Average Daily Notional: $9,987</li>
        <li>Today: $20,701 (~2x baseline, 6 months)</li>
        <li>The client has a clean history, past alerts in the past 6 months were cleared.</li>
    </ul>
    <div>Recommending to close and resolve as False Positive.</div>
    <div>Would you like to close and use this as the investigation summary?</div>
</div>`;

export const FINISH_UPDATING_INVESTIGATION_SUMMARY_TEMPLATE_MESSAGE = `<div>
    <div>Updating the investigation summary  and logging approval in Audit Trail.</div>
    <div>Case updated.</div>
</div>`;
